import { UserSettings } from "./UserSettings";
import { StorageUtils } from "./StorageUtils";
import { i18n } from "../I18n";
import * as I18nKeysShared from "../Locales/shared-map.json";
import { getRootUrl } from "./Utils";

export abstract class SessieChecker {
    private readonly storageItemKeyName = "message";

    constructor(
        private userSettings: UserSettings,
        private storage: Storage | null | undefined,
        private window: Window
    ) {
        if (storage && StorageUtils.isStorageAvailable(storage)) {
            this.window.addEventListener("storage", storageEvent => this.receive(storageEvent));
            this.announce();
        }
    }

    private receive(storageEvent: StorageEvent) {
        if (storageEvent.key !== this.storageItemKeyName) {
            return; // ignore other keys
        }

        const message = storageEvent.newValue ? (JSON.parse(storageEvent.newValue) as SessieMessage) : null;
        if (message && message.command === 'announce') {
            const localSessieData = this.getLocalSessieData();
            const remoteSessieData = message.data;

            const localSessieDataWijktAfVanMessageSessieData = localSessieData.sid !== '' && remoteSessieData.sid !== localSessieData.sid;
            if (localSessieDataWijktAfVanMessageSessieData) {
                // Men mag zo niet verder in dit venster, de pagina zal worden doorverwezen naar Default.aspx (en eventueel worden geredirect naar de loginpagina).
                if (document.forms.length) {
                    document.forms[0].disabled = true;
                }

                const tekst = this.createRemoteIngelogdTekst(localSessieData, remoteSessieData);
                this.showRemoteIngelogdMelding(tekst).always(() => {
                    const isRemoteIngelogd = remoteSessieData.sid !== "";
                    const loginUrl = this.userSettings.LoginUrl;
                    this.window.location.href = isRemoteIngelogd ? getRootUrl() + "/Default.aspx" : loginUrl;
                });
            }
        }
    }

    private announce() {
        const storage = this.storage as Storage;
        const message: SessieMessage = {
            command: 'announce',
            data: this.getLocalSessieData()
        };

        const storageItemKeyValue = JSON.stringify(message);
        storage.setItem(this.storageItemKeyName, storageItemKeyValue);
        storage.removeItem(this.storageItemKeyName);
    }

    private getLocalSessieData(): SessieData {
        let localSessieData: SessieData;
        if (document.forms.length) {
            localSessieData = {
                sid: this.userSettings.SessieId,
                dbalias: this.userSettings.DatabaseAlias,
                adminid: this.userSettings.AdminID,
                userid: this.userSettings.UserID
            }
        } else {
            localSessieData = {
                sid: null,
                dbalias: null,
                adminid: null,
                userid: null

            }
        }
        return localSessieData;
    }

    protected abstract showRemoteIngelogdMelding(tekst: string): JQueryPromise<void>;

    private createRemoteIngelogdTekst(localSessieData: SessieData, remoteSessieData: SessieData): string {
        let melding = i18n.t(I18nKeysShared.sessiecheck.message.error.opnieuwIngelogd);

        const verschillen = [];
        const isAndereDatabase = localSessieData.dbalias !== remoteSessieData.dbalias;
        if (isAndereDatabase) {
            verschillen.push(i18n.t(I18nKeysShared.sessiecheck.message.error.andereDatabase));
        } else {
            const isAndereAdministratie = remoteSessieData.adminid && remoteSessieData.adminid !== localSessieData.adminid;
            if (isAndereAdministratie) {
                verschillen.push(i18n.t(I18nKeysShared.sessiecheck.message.error.andereAdministratie));
            }

            const isAndereGebruiker = remoteSessieData.userid && remoteSessieData.userid !== "0" && remoteSessieData.userid !== localSessieData.userid;
            if (isAndereGebruiker) {
                verschillen.push(i18n.t(I18nKeysShared.sessiecheck.message.error.andereGebruikersnaam));
            }
        }

        if (verschillen.length > 0) {
            const scheidingsteken = ", ";
            let joined = verschillen.join(scheidingsteken);

            if (verschillen.length > 1) {
                const laatsteScheidingsteken = joined.lastIndexOf(scheidingsteken);
                joined = `${joined.substr(0, laatsteScheidingsteken)} ${i18n.t(I18nKeysShared.sessiecheck.message.error.en)}${joined.substr(laatsteScheidingsteken + 1)}`;
            }

            melding += " " + joined;
        }

        melding = `${melding}. ${i18n.t(I18nKeysShared.sessiecheck.message.error.schermVernieuwd)}`;
        return melding;
    }
}

interface SessieData {
    sid: string | null;
    dbalias: string | null;
    adminid: string | null;
    userid: string | null;
}

export interface SessieMessage {
    command: string;
    data: SessieData;
}
